import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Percent } from "lucide-react";
import { toast } from "sonner";

const SCOPES = [
    { v: "global", l: "Global (todos os produtos)" },
    { v: "category", l: "Categoria" },
    { v: "brand", l: "Marca" },
    { v: "product", l: "Produto (SKU)" },
];

const TYPES = [
    { v: "percent", l: "Percentagem (%)" },
    { v: "fixed", l: "Valor fixo (€ desconto)" },
    { v: "absolute", l: "Preço promocional manual (€)" },
];

const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const toIsoUtc = (local) => (local ? new Date(local).toISOString() : "");

const EMPTY = {
    name: "",
    scope: "global",
    target: "",
    discount_type: "percent",
    discount_value: 10,
    start_at: toLocalInput(new Date().toISOString()),
    end_at: toLocalInput(new Date(Date.now() + 7 * 86400000).toISOString()),
    active: true,
};

export default function AdminCampaigns() {
    const [list, setList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/campaigns").then((r) => setList(r.data));
    useEffect(() => {
        load();
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/products/brands").then((r) => setBrands(r.data.brands || []));
    }, []);

    const save = async (data) => {
        const payload = {
            ...data,
            discount_value: Number(data.discount_value),
            start_at: toIsoUtc(data.start_at),
            end_at: toIsoUtc(data.end_at),
            target: data.scope === "global" ? null : data.target || null,
        };
        try {
            if (data.id) await api.put(`/admin/campaigns/${data.id}`, payload);
            else await api.post("/admin/campaigns", payload);
            toast.success("Campanha guardada");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao guardar");
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminar campanha?")) return;
        await api.delete(`/admin/campaigns/${id}`);
        toast.success("Eliminada");
        load();
    };

    const isLive = (c) => {
        const now = new Date();
        return c.active && new Date(c.start_at) <= now && now <= new Date(c.end_at);
    };

    return (
        <div data-testid="admin-campaigns-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-black text-4xl tracking-tighter">Campanhas</h1>
                    <p className="text-sm text-ink/60 mt-1">
                        Descontos só ficam visíveis no website durante o período de uma campanha activa.
                    </p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="new-campaign-btn">
                    <Plus size={16} /> Nova Campanha
                </button>
            </div>

            <div className="border-2 border-ink bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink text-white text-xs font-mono uppercase tracking-wider">
                        <tr>
                            <th className="text-left p-3">Estado</th>
                            <th className="text-left p-3">Nome</th>
                            <th className="text-left p-3">Âmbito</th>
                            <th className="text-left p-3">Desconto</th>
                            <th className="text-left p-3">Início</th>
                            <th className="text-left p-3">Fim</th>
                            <th className="text-right p-3">Acções</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((c) => (
                            <tr key={c.id} className="border-t-2 border-ink/10 hover:bg-magenta/5" data-testid={`campaign-row-${c.id}`}>
                                <td className="p-3">
                                    <span className={`text-xs font-mono px-2 py-1 ${
                                        isLive(c) ? "bg-accent-green text-white" : c.active ? "bg-paper border border-ink" : "bg-ink/10 text-ink/60"
                                    }`}>
                                        {isLive(c) ? "ACTIVA" : c.active ? "AGENDADA" : "INACTIVA"}
                                    </span>
                                </td>
                                <td className="p-3 font-bold">{c.name}</td>
                                <td className="p-3 text-xs">
                                    {SCOPES.find((s) => s.v === c.scope)?.l || c.scope}
                                    {c.target && <div className="font-mono text-ink/60 text-[11px]">→ {c.target}</div>}
                                </td>
                                <td className="p-3 font-mono">
                                    {c.discount_type === "percent" && `-${c.discount_value}%`}
                                    {c.discount_type === "fixed" && `-${c.discount_value} €`}
                                    {c.discount_type === "absolute" && `${c.discount_value} € fixo`}
                                </td>
                                <td className="p-3 text-xs font-mono">{new Date(c.start_at).toLocaleString("pt-PT")}</td>
                                <td className="p-3 text-xs font-mono">{new Date(c.end_at).toLocaleString("pt-PT")}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEditing({ ...c, start_at: toLocalInput(c.start_at), end_at: toLocalInput(c.end_at) })} className="p-1 hover:text-magenta" data-testid={`edit-campaign-${c.id}`}>
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => del(c.id)} className="p-1 hover:text-destructive ml-1" data-testid={`delete-campaign-${c.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && (
                            <tr>
                                <td colSpan={7} className="p-8 text-center text-ink/50 font-mono text-sm">
                                    Sem campanhas. Crie uma para activar descontos no website.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <CampaignModal
                    initial={editing}
                    categories={categories}
                    brands={brands}
                    onClose={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </div>
    );
}

function CampaignModal({ initial, categories, brands, onClose, onSave }) {
    const [form, setForm] = useState(initial);
    const upd = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="campaign-modal">
            <div className="bg-white border-2 border-ink w-full max-w-2xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex items-center justify-between">
                    <h2 className="font-display font-black text-2xl flex items-center gap-2">
                        <Percent size={22} /> {form.id ? "Editar campanha" : "Nova campanha"}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:text-magenta">
                        <X size={20} />
                    </button>
                </div>
                <form
                    onSubmit={(e) => { e.preventDefault(); onSave(form); }}
                    className="p-6 grid sm:grid-cols-2 gap-4"
                >
                    <div className="sm:col-span-2">
                        <label className="block">
                            <span className="label-mono mb-2 block">Nome</span>
                            <input value={form.name} onChange={upd("name")} required className="input-edge" data-testid="campaign-name-input" />
                        </label>
                    </div>
                    <label className="block">
                        <span className="label-mono mb-2 block">Âmbito</span>
                        <select value={form.scope} onChange={upd("scope")} className="input-edge" data-testid="campaign-scope-select">
                            {SCOPES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                    </label>
                    {form.scope === "category" && (
                        <label className="block">
                            <span className="label-mono mb-2 block">Categoria</span>
                            <select value={form.target || ""} onChange={upd("target")} required className="input-edge">
                                <option value="">—</option>
                                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                            </select>
                        </label>
                    )}
                    {form.scope === "brand" && (
                        <label className="block">
                            <span className="label-mono mb-2 block">Marca</span>
                            <select value={form.target || ""} onChange={upd("target")} required className="input-edge">
                                <option value="">—</option>
                                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </label>
                    )}
                    {form.scope === "product" && (
                        <label className="block">
                            <span className="label-mono mb-2 block">SKU do produto</span>
                            <input value={form.target || ""} onChange={upd("target")} required className="input-edge" placeholder="ex: WLHPTNC2612A" />
                        </label>
                    )}
                    <label className="block">
                        <span className="label-mono mb-2 block">Tipo</span>
                        <select value={form.discount_type} onChange={upd("discount_type")} className="input-edge" data-testid="campaign-type-select">
                            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="label-mono mb-2 block">Valor</span>
                        <input type="number" step="0.01" value={form.discount_value} onChange={upd("discount_value")} required className="input-edge" data-testid="campaign-value-input" />
                    </label>
                    <label className="block">
                        <span className="label-mono mb-2 block">Início</span>
                        <input type="datetime-local" value={form.start_at} onChange={upd("start_at")} required className="input-edge" />
                    </label>
                    <label className="block">
                        <span className="label-mono mb-2 block">Fim</span>
                        <input type="datetime-local" value={form.end_at} onChange={upd("end_at")} required className="input-edge" />
                    </label>
                    <label className="sm:col-span-2 flex items-center gap-2">
                        <input type="checkbox" checked={!!form.active} onChange={upd("active")} className="accent-magenta w-4 h-4" />
                        <span className="text-sm font-bold uppercase">Activa</span>
                    </label>

                    <div className="sm:col-span-2 flex gap-3 justify-end pt-4 border-t-2 border-ink">
                        <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button type="submit" className="btn-primary" data-testid="save-campaign-btn">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
